/**
 * The one control for attaching tags to something.
 *
 * Shared by the memory editor, the agent editor and the project block rather
 * than reimplemented three times, because the thing it has to teach is subtle
 * and easy to get wrong in prose: ticking two tags in the *same* namespace
 * widens what matches, ticking them in *different* namespaces narrows it. A
 * single control that always groups by namespace makes that visible without
 * anyone reading a paragraph about it.
 *
 * Untagged is a real, common and correct state — it means "available
 * everywhere" — so the empty case says so instead of nagging.
 */
import type { Tag, TagId } from "@taskin-code/contracts";
import { TAG_MAX_PER_TARGET, formatTagRef } from "@taskin-code/contracts";
import { useMemo } from "react";

import { groupTagsByNamespace } from "./TagsSection.logic";

export function TagPicker(props: {
  readonly available: ReadonlyArray<Tag>;
  readonly selected: ReadonlyArray<TagId>;
  readonly disabled?: boolean;
  readonly onChange: (next: ReadonlyArray<TagId>) => void;
}) {
  const groups = useMemo(() => groupTagsByNamespace(props.available), [props.available]);
  const selected = useMemo(() => new Set(props.selected), [props.selected]);

  if (props.available.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No hay etiquetas definidas todavía. Créalas en Ajustes → Etiquetas.
      </p>
    );
  }

  const toggle = (tagId: TagId) => {
    if (props.disabled === true) return;
    if (selected.has(tagId)) {
      props.onChange(props.selected.filter((id) => id !== tagId));
      return;
    }
    if (props.selected.length >= TAG_MAX_PER_TARGET) return;
    props.onChange([...props.selected, tagId]);
  };

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <div key={group.namespace} className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {group.namespace === "" ? "Sin namespace" : group.namespace}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {group.tags.map((tag) => {
              const isOn = selected.has(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  disabled={props.disabled === true}
                  onClick={() => toggle(tag.id)}
                  title={tag.description ?? formatTagRef(tag)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                    isOn
                      ? "border-primary/60 bg-primary/15 text-foreground"
                      : "border-border/60 bg-muted/30 text-muted-foreground hover:bg-muted/60"
                  } ${props.disabled === true ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  {tag.color !== null && (
                    <span
                      aria-hidden
                      className="size-2 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                  )}
                  {tag.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <p className="text-xs text-muted-foreground">
        {props.selected.length === 0
          ? "Sin etiquetas: disponible para todos los agentes y proyectos."
          : "Dentro de un mismo grupo basta con que cruce una; entre grupos tienen que cruzar todos."}
      </p>
    </div>
  );
}
